// screens/HomeScreen.js - Modification pour afficher les onglets cachés

import React, { useContext, useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  RefreshControl,
  Alert
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { SheetContext } from '../contexts/SheetContext';
import { AuthContext } from '../contexts/AuthContext';
import PWAInstallMessage from '../components/PWAInstallMessage';
import LoadingScreen from '../components/LoadingScreen';
import alertService from '../services/alertService';
import hapticService from '../services/hapticService';
import sheetService from '../services/sheetService';
import { useRetryCountdown } from '../hooks/useRetryCountdown';
import { config } from '../config';

const HomeScreen = ({ navigation }) => {
  const { 
    competitions, 
    isLoading, 
    error, 
    fetchCompetitions,
    // 🆕 Nouvelles propriétés pour les onglets cachés
    showHiddenSheets,
    hiddenSheetsCount,
    canManageHiddenSheets
  } = useContext(SheetContext);
  const { user, getValidAccessToken, signOut } = useContext(AuthContext);
  const [searchText, setSearchText] = useState('');
  const [filteredCompetitions, setFilteredCompetitions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [errorType, setErrorType] = useState(null); // 'session', 'server', 'network'

  // Hook pour le countdown du retry
  const { getButtonText, isDisabled, startCountdown, resetCountdown } = useRetryCountdown(config.RETRY_COUNTDOWN_SECONDS);

  // Reset search when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      setSearchText('');
    }, [])
  );

  // Démarrer le countdown seulement pour les erreurs non-session
  useEffect(() => {
    if (error && errorType && errorType !== 'session') {
      startCountdown();
    } else {
      resetCountdown();
    }
  }, [error, errorType, startCountdown, resetCountdown]);

  // Wrapper pour fetchCompetitions avec détection d'erreur
  const fetchWithErrorHandling = useCallback(async () => {
    try {
      setErrorType(null);
      await fetchCompetitions();
    } catch (e) {
      if (e instanceof sheetService.ApiError) {
        setErrorType(e.type);
      } else {
        setErrorType('network');
      }
      console.error('Fetch error:', e);
    }
  }, [fetchCompetitions]);

  // Filter competitions based on search text
  useEffect(() => {
    if (!searchText) {
      setFilteredCompetitions(competitions);
    } else {
      const filtered = competitions.filter(competition =>
        competition.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredCompetitions(filtered);
    }
  }, [searchText, competitions]);

  // Pull to refresh handler
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchWithErrorHandling();
    } finally {
      setRefreshing(false);
    }
  }, [fetchWithErrorHandling]);

  // Navigate to competition screen
  const handleCompetitionPress = (competitionName, isHidden) => {
    // 🆕 Avertissement pour les onglets cachés
    if (isHidden) {
      alertService.showAlert(
        'Hidden Sheet',
        `"${competitionName}" is a hidden sheet. Are you sure you want to access it?`,
        [
          { 
            text: 'Cancel', 
            style: 'cancel',
            onPress: () => hapticService.button()
          },
          { 
            text: 'Continue', 
            onPress: () => {
              hapticService.listItem();
              navigation.navigate('Selection', { competitionName });
            }
          }
        ]
      );
      return;
    }

    hapticService.listItem();
    navigation.navigate('Selection', { competitionName });
  };

  const handleRetry = async () => {
    if (isDisabled || errorType === 'session') return; // Pas de retry pour erreurs session
    
    hapticService.button();
    try {
      await fetchWithErrorHandling();
      resetCountdown(); // Reset le countdown en cas de succès
    } catch (e) {
      hapticService.error();
      alertService.showAlert('Error', 'Failed to retry. Please check your connection and try again.');
    }
  };

  // Clear search text
  const clearSearch = () => {
    hapticService.button();
    setSearchText('');
  };

  // 🆕 Render each competition item avec indicateur pour onglets cachés
  const renderCompetitionItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.competitionItem,
        item.hidden && styles.competitionItemHidden // 🆕 Style pour onglets cachés
      ]}
      onPress={() => handleCompetitionPress(item.name, item.hidden)}
    >
      <View style={styles.competitionInfo}>
        <View style={styles.competitionNameContainer}>
          <Text style={[
            styles.competitionName,
            item.hidden && styles.competitionNameHidden // 🆕 Style pour nom d'onglet caché
          ]}>
            {item.name}
          </Text>
          {/* 🆕 Indicateur d'onglet caché */}
          {item.hidden && (
            <View style={styles.hiddenBadge}>
              <Ionicons name="eye-off-outline" size={14} color="#ff9800" />
              <Text style={styles.hiddenBadgeText}>Hidden</Text>
            </View>
          )}
        </View>
        <View style={styles.ridersInfo}>
          <Ionicons name="person-outline" size={16} color="#666" />
          <Text style={styles.ridersCount}>{item.ridersCount} pilots</Text>
        </View>
      </View>
      <Ionicons 
        name="chevron-forward" 
        size={22} 
        color={item.hidden ? "#ff9800" : "#0066cc"} // 🆕 Couleur différente pour onglets cachés
      />
    </TouchableOpacity>
  );

  // Fonction pour obtenir le message d'erreur approprié
  const getErrorMessage = () => {
    if (errorType === 'session') {
      return 'Session expired. Please sign in again.';
    }
    return error;
  };

  // Déterminer si on doit afficher le bouton retry
  const shouldShowRetry = error && errorType && errorType !== 'session';

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Available Races</Text>
        {/* 🆕 Affichage du statut des onglets cachés pour les admins */}
        {canManageHiddenSheets && hiddenSheetsCount > 0 && (
          <View style={styles.hiddenSheetsInfo}>
            <Ionicons name="eye-off-outline" size={16} color="#ff9800" />
            <Text style={styles.hiddenSheetsText}>
              {showHiddenSheets ? `${hiddenSheetsCount} hidden shown` : `${hiddenSheetsCount} hidden`}
            </Text>
          </View>
        )}
      </View>

      <PWAInstallMessage />

      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#666" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search Races..."
          value={searchText}
          onChangeText={setSearchText}
        />
        {searchText.length > 0 && (
          <TouchableOpacity onPress={clearSearch} style={styles.clearButton}>
            <Ionicons name="close-circle" size={20} color="#999" />
          </TouchableOpacity>
        )}
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{getErrorMessage()}</Text>
          {errorType === 'session' ? (
            <TouchableOpacity 
              style={styles.retryButton} 
              onPress={async () => {
                hapticService.button();
                await signOut();
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'Login' }]
                });
              }}
            >
              <Text style={styles.retryText}>Reconnect</Text>
            </TouchableOpacity>
          ) : (shouldShowRetry && (
            <TouchableOpacity 
              style={[
                styles.retryButton, 
                isDisabled && styles.retryButtonDisabled
              ]} 
              onPress={handleRetry}
              disabled={isDisabled}
            >
              <Text style={[
                styles.retryText,
                isDisabled && styles.retryTextDisabled
              ]}>
                {getButtonText()}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Loading uniforme avec le nouveau composant */}
      {isLoading && !refreshing ? (
        <LoadingScreen message="Loading competitions..." variant="content" />
      ) : (
        <FlatList
          data={filteredCompetitions}
          renderItem={renderCompetitionItem}
          keyExtractor={(item) => item.name}
          contentContainerStyle={styles.competitionsList}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={
            <View style={styles.emptyList}>
              <Ionicons name="document-text-outline" size={48} color="#ccc" />
              <Text style={styles.emptyText}>
                {searchText
                  ? 'No Races match your search'
                  : 'No Races available'}
              </Text>
              {/* 🆕 Message pour les onglets cachés */}
              {canManageHiddenSheets && hiddenSheetsCount > 0 && !showHiddenSheets && (
                <Text style={styles.hiddenSheetsHint}>
                  {hiddenSheetsCount} hidden race{hiddenSheetsCount > 1 ? 's' : ''} available.
                  Enable in Settings to show them.
                </Text>
              )}
            </View>
          }
        />
      )}

      <View style={styles.footer}>
        <Text style={styles.copyright}>© Gabriele Fusco / VTM 2025</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  title: {
    fontSize: 20,
    
    color: '#333',
    fontFamily: 'Montserrat_700Bold',
  },
  // 🆕 Styles pour l'info des onglets cachés
  hiddenSheetsInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff3e0',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#ff9800',
  },
  hiddenSheetsText: {
    fontSize: 12,
    color: '#ff9800',
    fontFamily: 'Montserrat_500Medium',
    marginLeft: 4,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    margin: 16,
    marginBottom: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 46,
    fontSize: 16,
    fontFamily: 'Montserrat_400Regular',
    outline: 'none',
  },
  clearButton: {
    padding: 4,
    marginLeft: 8,
  },
  errorContainer: {
    margin: 16,
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#ffebee',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  errorText: {
    color: '#d32f2f',
    flex: 1,
    fontFamily: 'Montserrat_400Regular',
  },
  retryButton: {
    backgroundColor: '#0066cc',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  retryButtonDisabled: {
    backgroundColor: '#cccccc',
  },
  retryText: {
    color: '#fff',
    
    fontFamily: 'Montserrat_700Bold',
  },
  retryTextDisabled: {
    color: '#999999',
  },
  competitionsList: {
    padding: 16,
    paddingTop: 8,
  },
  competitionItem: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  // 🆕 Style pour les onglets cachés
  competitionItemHidden: {
    backgroundColor: '#fff8f0',
    borderLeftWidth: 4,
    borderLeftColor: '#ff9800',
  },
  competitionInfo: {
    flex: 1,
  },
  // 🆕 Container pour le nom et le badge
  competitionNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  competitionName: {
    fontSize: 18,
    
    color: '#333',
    fontFamily: 'Montserrat_600SemiBold',
    marginRight: 8,
  },
  // 🆕 Style pour nom d'onglet caché
  competitionNameHidden: {
    color: '#f57c00',
  },
  // 🆕 Badge pour onglets cachés
  hiddenBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff3e0',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#ff9800',
  },
  hiddenBadgeText: {
    fontSize: 10,
    color: '#ff9800',
    fontFamily: 'Montserrat_600SemiBold',
    marginLeft: 2,
  },
  ridersInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ridersCount: {
    marginLeft: 6,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
  emptyList: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    color: '#888',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  // 🆕 Message pour les onglets cachés
  hiddenSheetsHint: {
    marginTop: 12,
    fontSize: 14,
    color: '#ff9800',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
    fontStyle: 'italic',
  },
  footer: {
    padding: 16,
    alignItems: 'center',
  },
  copyright: {
    fontSize: 12,
    color: '#888',
    fontFamily: 'Montserrat_400Regular',
  },
});

export default HomeScreen;